// 全局数字键盘初始化文件
// 直接从源模块导入，避免通过index.js导入
import { getKeypadManager } from "../rlMxModules/rlDataClass/KeypadManager";
import { isMobileDevice } from "@/utils/matchMedia";

/**
 * 初始化全局数字键盘管理器
 * 将拦截带有特定数据属性的输入框，显示自定义数字键盘
 * 支持的数据属性：
 * - data-use-custom-keypad: 向后兼容的标识符
 * - data-numeric-keypad: 新的统一标识符
 * - data-rl-keypad: 简化标识符
 *
 * 高级用法：
 * - data-rl-keypad-target="selector": 在父元素上指定，表示只有匹配选择器的子元素才会被拦截
 * - data-no-custom-keypad 或 data-rl-keypad-disabled: 用于明确排除元素不被拦截
 *
 * 示例：
 * <input data-numeric-keypad type="number"> <!-- 此输入框将使用数字键盘 -->
 * <div data-rl-keypad data-rl-keypad-target="input[type='number']"> <!-- 所有数字输入框子元素将使用数字键盘 -->
 *   <input type="number">
 *   <input type="text" data-no-custom-keypad> <!-- 此输入框不会被拦截 -->
 * </div>
 *
 * @param {object} options - 配置选项
 * @returns {object} - 键盘管理器实例
 */
export function initGlobalNumericKeypad(options = {}) {
  // 非移动设备下不初始化数字键盘，除非强制初始化
  if (!isMobileDevice() && !options.forceInitialize) {
    console.log("非移动设备，不初始化数字键盘");
    return {};
  }

  const keypadManager = getKeypadManager({
    // 基于标识符拦截，默认不使用选择器
    selector: "",
    // 可自定义数据属性列表
    dataAttributes: options.dataAttributes || [
      "data-use-custom-keypad", // 向后兼容
      "data-numeric-keypad", // 新的统一标识符
      "data-rl-keypad", // 简化版
    ],
    // 默认排除的选择器
    excludeSelectors:
      options.excludeSelectors ||
      'input[type="password"], input[type="file"], input[type="checkbox"], input[type="radio"], input[type="hidden"]',
    // 默认启用
    enabled: options.enabled !== false,
    // 默认启用实时同步
    realTimeSync: options.realTimeSync !== false,
    // 数字键盘配置

    keypadOptions: {
      width: options.width || 260,
      height: options.height || 330,
      maxLength: options.maxLength || 100,
      allowDecimal: options.allowDecimal !== false,
      theme: options.theme || "light",
      zIndex: options.zIndex || 10000,
      onRealTimeSync: options.realTimeSync !== false, // 启用实时同步
      disableLeadingZero: options.disableLeadingZero || false, // 是否禁止以0开头
    },
  });

  console.log("全局数字键盘管理器已初始化，使用基于标识符的拦截");
  return keypadManager;
}

/**
 * 启用全局数字键盘
 * @returns {object} - 键盘管理器实例
 */
export function enableGlobalNumericKeypad() {
  // 非移动设备下不启用数字键盘
  if (!isMobileDevice()) {
    console.log("非移动设备，不启用数字键盘");
    return {};
  }

  const keypadManager = getKeypadManager();
  keypadManager.enable();
  console.log("全局数字键盘已启用");
  return keypadManager;
}

/**
 * 禁用全局数字键盘
 * @returns {object} - 键盘管理器实例
 */
export function disableGlobalNumericKeypad() {
  try {
    const keypadManager = getKeypadManager();
    keypadManager.disable();
    console.log("全局数字键盘已禁用");
    return keypadManager;
  } catch (e) {
    console.log("未找到数字键盘实例或禁用失败");
    return {};
  }
}

/**
 * 在应用程序入口处调用此函数，以初始化全局数字键盘
 * 此函数将拦截带有特定数据属性的输入框，使用自定义数字键盘替代系统键盘
 * 仅在移动设备上启用
 */
export function setupGlobalNumericKeypad() {
  // 在DOM加载完成后初始化
  document.addEventListener("DOMContentLoaded", () => {
    // 非移动设备下不设置数字键盘
    if (!isMobileDevice()) {
      console.log("非移动设备，不设置数字键盘");
      return;
    }

    // 初始化全局数字键盘
    const keypadManager = initGlobalNumericKeypad({
      // 基于标识符拦截，强制使用空选择器
      selector: "",
      // 数据属性标识符
      dataAttributes: [
        "data-use-custom-keypad",
        "data-numeric-keypad",
        "data-rl-keypad",
      ],
      // 键盘配置
      width: 260,
      height: 350,
      maxLength: 100,
      allowDecimal: true,
      theme: "light",
      zIndex: 10000,
      // 启用实时同步
      realTimeSync: true,
      // 启用禁止以0开头
      disableLeadingZero: true,
    });

    // 注入全局样式，帮助阻止系统键盘弹出
    _injectGlobalStyles();

    console.log("全局数字键盘已设置完成（使用基于标识符的拦截）");
  });
}

/**
 * 注入全局样式辅助阻止系统键盘
 * @private
 */
function _injectGlobalStyles() {
  const styleElement = document.createElement("style");
  styleElement.textContent = `
    /* 数字键盘标识符样式 */
    [data-numeric-keypad],
    [data-use-custom-keypad],
    [data-rl-keypad] {
      cursor: pointer !important;
    }
    
    /* 标识符目标样式 */
    [data-rl-keypad] input:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-numeric-keypad] input:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-use-custom-keypad] input:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]) {
      cursor: pointer !important;
    }
    
    /* iOS特殊处理 */
    @supports (-webkit-touch-callout: none) {
      [data-numeric-keypad]:focus,
      [data-use-custom-keypad]:focus,
      [data-rl-keypad]:focus,
      [data-rl-keypad] input:focus:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
      [data-numeric-keypad] input:focus:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
      [data-use-custom-keypad] input:focus:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]) {
        font-size: 16px !important; /* 防止iOS缩放 */
        -webkit-user-select: none !important;
      }
    }
    
    /* Element UI组件样式覆盖 */
    .el-input[data-numeric-keypad] .el-input__inner,
    .el-input[data-use-custom-keypad] .el-input__inner,
    .el-input[data-rl-keypad] .el-input__inner,
    .el-input-number[data-numeric-keypad] .el-input__inner,
    .el-input-number[data-use-custom-keypad] .el-input__inner,
    .el-input-number[data-rl-keypad] .el-input__inner,
    [data-rl-keypad] .el-input .el-input__inner:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-numeric-keypad] .el-input .el-input__inner:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-use-custom-keypad] .el-input .el-input__inner:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-rl-keypad] .el-input-number .el-input__inner:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-numeric-keypad] .el-input-number .el-input__inner:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]),
    [data-use-custom-keypad] .el-input-number .el-input__inner:not([data-no-custom-keypad]):not([data-rl-keypad-disabled]) {
      cursor: pointer !important;
    }
  `;
  document.head.appendChild(styleElement);
}
